/**
 * VisitorDashboard - Main dashboard component for visitor management
 * Provides overview, analytics, and quick actions for visitor operations
 */

import React, { useState, useEffect } from 'react';
import {
  Users,
  UserCheck,
  UserX,
  Clock,
  Building2,
  Calendar,
  BarChart3,
  Home,
  Plus,
  Search,
  Filter,
  Download,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Eye,
  UserPlus,
  Activity,
  Building,
  Edit,
  Trash2,
  X,
  FileText,
  QrCode,
  Camera
} from 'lucide-react';
import useVisitorData from './hooks/useVisitorData';
import useVisitorPermissions from './hooks/useVisitorPermissions';
import useVisitorOffline from './hooks/useVisitorOffline';
import VisitorRegistrationForm from './VisitorRegistrationForm';
import VisitorOfflineSync from './VisitorOfflineSync';

const VisitorDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [buildingFilter, setBuildingFilter] = useState('all');
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [showVisitorModal, setShowVisitorModal] = useState(false);

  // Custom hooks
  const {
    visitors,
    activeVisitors,
    loading,
    error,
    isOffline,
    fetchVisitors,
    fetchActiveVisitors,
    registerVisitor,
    getVisitorById,
    setError
  } = useVisitorData();

  const {
    permissions,
    canPerformAction,
    getAccessibleBuildings,
    isFeatureAvailable
  } = useVisitorPermissions();

  const {
    pendingActions,
    syncStatus,
    lastSyncTime,
    offlineStats,
    triggerSync
  } = useVisitorOffline();

  // Dashboard statistics
  const [dashboardStats, setDashboardStats] = useState({
    total_visitors: 0,
    active_visitors: 0,
    pending_approval: 0,
    checked_in_today: 0,
    total_buildings: 0
  });

  // Load dashboard data
  useEffect(() => {
    loadDashboardData();
  }, [refreshKey]);

  // Calculate dashboard statistics
  useEffect(() => {
    if (visitors.length > 0) {
      const today = new Date().toDateString();
      const stats = {
        total_visitors: visitors.length,
        active_visitors: visitors.filter(v => v.status === 'checked_in').length,
        pending_approval: visitors.filter(v => v.status === 'pending').length,
        checked_in_today: visitors.filter(v => {
          const checkinDate = v.checked_in_at ? new Date(v.checked_in_at).toDateString() : null;
          return checkinDate === today;
        }).length,
        total_buildings: [...new Set(visitors.map(v => v.property_id))].length
      };
      setDashboardStats(stats);
    }
  }, [visitors]);

  const loadDashboardData = async () => {
    try {
      await Promise.all([
        fetchVisitors(),
        fetchActiveVisitors()
      ]);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    }
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    if (!isOffline) {
      triggerSync();
    }
  };

  const handleRegistrationSuccess = (newVisitor) => {
    setShowRegistrationForm(false);
    // Update stats (visitors state is already updated by registerVisitor)
    setDashboardStats(prev => ({
      ...prev,
      total_visitors: prev.total_visitors + 1,
      pending_approval: prev.pending_approval + 1
    }));
    // Only trigger sync if offline to sync pending actions
    if (!isOffline) {
      triggerSync();
    }
  };

  const handleViewVisitor = async (visitorId) => {
    try {
      const visitorDetails = await getVisitorById(visitorId);
      setSelectedVisitor(visitorDetails);
      setShowVisitorModal(true);
    } catch (error) {
      console.error('Error fetching visitor details:', error);
      setError('Failed to load visitor details');
    }
  };

  // Format relative time
  const formatRelativeTime = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffInMilliseconds = now - date;
    const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    if (diffInDays < 7) return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  // Get status color
  const getStatusColor = (status) => {
    const colorMap = {
      'pending': '#F59E0B',
      'approved': '#10B981',
      'rejected': '#EF4444',
      'checked_in': '#00C5CB',
      'checked_out': '#6B7280',
      'expired': '#9CA3AF'
    };
    return colorMap[status] || '#6B7280';
  };

  // Recent visitors for activity feed
  const recentVisitors = visitors
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 8);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Visitor Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isOffline && (
              <span className="inline-flex items-center gap-1 text-orange-600">
                <AlertTriangle className="w-4 h-4" />
                Offline Mode
              </span>
            )}
            {!isOffline && pendingActions.length > 0 && (
              <span className="inline-flex items-center gap-1 text-blue-600">
                <RefreshCw className="w-4 h-4" />
                {pendingActions.length} pending sync{pendingActions.length !== 1 ? 's' : ''}
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
          {canPerformAction('register') && (
            <button
              onClick={() => setShowRegistrationForm(true)}
              className="inline-flex items-center justify-center gap-2 px-4 py-3 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base min-h-[44px] sm:min-h-[auto]"
            >
              <UserPlus className="w-4 h-4" />
              Register Visitor
            </button>
          )}
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 px-4 py-3 sm:py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base min-h-[44px] sm:min-h-[auto]"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Offline Sync Component */}
      {(isOffline || pendingActions.length > 0) && (
        <VisitorOfflineSync />
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto scrollbar-hide">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-3 px-2 sm:px-1 border-b-2 font-medium text-sm whitespace-nowrap min-h-[44px] flex items-center ${
              activeTab === 'overview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Home className="inline-block w-4 h-4 mr-2" />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('visitors')}
            className={`py-3 px-2 sm:px-1 border-b-2 font-medium text-sm whitespace-nowrap min-h-[44px] flex items-center ${
              activeTab === 'visitors'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Users className="inline-block w-4 h-4 mr-2" />
            All Visitors
          </button>
          {isFeatureAvailable('analytics') && (
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-3 px-2 sm:px-1 border-b-2 font-medium text-sm whitespace-nowrap min-h-[44px] flex items-center ${
                activeTab === 'analytics'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <BarChart3 className="inline-block w-4 h-4 mr-2" />
              Analytics
            </button>
          )}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Visitors</p>
                  {loading ? (
                    <div className="animate-pulse">
                      <div className="h-8 bg-gray-200 rounded w-16 mt-1"></div>
                    </div>
                  ) : (
                    <p className="text-2xl font-bold text-gray-900">{dashboardStats.total_visitors}</p>
                  )}
                </div>
                <Users className="h-8 w-8" style={{ color: '#00C5CB' }} />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Visitors</p>
                  {loading ? (
                    <div className="animate-pulse">
                      <div className="h-8 bg-gray-200 rounded w-16 mt-1"></div>
                    </div>
                  ) : (
                    <p className="text-2xl font-bold text-gray-900">{dashboardStats.active_visitors}</p>
                  )}
                </div>
                <UserCheck className="h-8 w-8 text-green-500" />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Pending Approval</p>
                  {loading ? (
                    <div className="animate-pulse">
                      <div className="h-8 bg-gray-200 rounded w-16 mt-1"></div>
                    </div>
                  ) : (
                    <p className="text-2xl font-bold text-gray-900">{dashboardStats.pending_approval}</p>
                  )}
                </div>
                <Clock className="h-8 w-8 text-orange-500" />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Checked In Today</p>
                  {loading ? (
                    <div className="animate-pulse">
                      <div className="h-8 bg-gray-200 rounded w-16 mt-1"></div>
                    </div>
                  ) : (
                    <p className="text-2xl font-bold text-gray-900">{dashboardStats.checked_in_today}</p>
                  )}
                </div>
                <Calendar className="h-8 w-8" style={{ color: '#00C5CB' }} />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Buildings</p>
                  {loading ? (
                    <div className="animate-pulse">
                      <div className="h-8 bg-gray-200 rounded w-16 mt-1"></div>
                    </div>
                  ) : (
                    <p className="text-2xl font-bold text-gray-900">{dashboardStats.total_buildings}</p>
                  )}
                </div>
                <Building2 className="h-8 w-8" style={{ color: '#00C5CB' }} />
              </div>
            </div>
          </div>

          {/* Active Visitors and Recent Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Active Visitors */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Active Visitors</h2>
                <button
                  onClick={() => setActiveTab('visitors')}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  View All
                </button>
              </div>

              {loading ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="animate-pulse flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-1"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : activeVisitors.length === 0 ? (
                <div className="text-center py-8">
                  <UserX className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                  <p className="text-gray-500">No active visitors</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {activeVisitors.slice(0, 5).map((visitor) => (
                    <div key={visitor.id} className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <Users className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {visitor.first_name} {visitor.last_name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {visitor.company} • Checked in {formatRelativeTime(visitor.checked_in_at)}
                        </p>
                      </div>
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: getStatusColor(visitor.status) }}
                      ></div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
                <button
                  onClick={handleRefresh}
                  className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                  ) : (
                    "Refresh"
                  )}
                </button>
              </div>

              {loading ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="animate-pulse flex items-center space-x-3">
                      <div className="w-2 h-2 bg-gray-200 rounded-full"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      </div>
                      <div className="h-4 bg-gray-200 rounded w-16"></div>
                    </div>
                  ))}
                </div>
              ) : error ? (
                <div className="text-center py-8">
                  <AlertTriangle className="mx-auto h-8 w-8 text-red-500 mb-2" />
                  <p className="text-gray-500">{error}</p>
                  <button
                    onClick={handleRefresh}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                  >
                    Try again
                  </button>
                </div>
              ) : recentVisitors.length === 0 ? (
                <div className="text-center py-8">
                  <Activity className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                  <p className="text-gray-500">No recent activity</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentVisitors.map((visitor) => (
                    <div key={visitor.id} className="flex items-center space-x-3 text-sm">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: getStatusColor(visitor.status) }}
                      ></div>
                      <span className="text-gray-600 flex-1">
                        {visitor.first_name} {visitor.last_name} from {visitor.company} - {visitor.status}
                      </span>
                      <span className="text-gray-400">
                        {formatRelativeTime(visitor.created_at)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'visitors' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <h2 className="text-lg font-semibold text-gray-900">All Visitors</h2>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search visitors..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full sm:w-auto pl-10 pr-4 py-3 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base min-h-[44px] sm:min-h-[auto]"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full sm:w-auto px-3 py-3 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base min-h-[44px] sm:min-h-[auto]"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="checked_in">Checked In</option>
                  <option value="checked_out">Checked Out</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                <span className="ml-2 text-gray-600">Loading visitors...</span>
              </div>
            ) : visitors.filter(visitor => {
              const matchesSearch = `${visitor.first_name} ${visitor.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                  visitor.company.toLowerCase().includes(searchTerm.toLowerCase());
              const matchesStatus = statusFilter === 'all' || visitor.status === statusFilter;
              return matchesSearch && matchesStatus;
            }).length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No visitors found</p>
                <p className="text-sm text-gray-400 mt-1">Try adjusting your search or filters</p>
              </div>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                {visitors.filter(visitor => {
                  const matchesSearch = `${visitor.first_name} ${visitor.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                      visitor.company.toLowerCase().includes(searchTerm.toLowerCase());
                  const matchesStatus = statusFilter === 'all' || visitor.status === statusFilter;
                  return matchesSearch && matchesStatus;
                }).map((visitor) => (
                  <div key={visitor.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 gap-3 sm:gap-4">
                    <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Users className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium text-gray-900 text-sm sm:text-base truncate">{visitor.first_name} {visitor.last_name}</h3>
                        <p className="text-sm text-gray-500 truncate">{visitor.company}</p>
                        <p className="text-xs text-gray-400">
                          {visitor.checked_in_at ? `Checked in ${formatRelativeTime(visitor.checked_in_at)}` : 
                           `Registered ${formatRelativeTime(visitor.created_at)}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-2">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-center sm:text-left ${
                        visitor.status === 'checked_in' ? 'bg-green-100 text-green-800' :
                        visitor.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        visitor.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                        visitor.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {visitor.status === 'checked_in' ? 'Checked In' :
                         visitor.status === 'pending' ? 'Pending' :
                         visitor.status === 'approved' ? 'Approved' :
                         visitor.status === 'rejected' ? 'Rejected' :
                         'Checked Out'}
                      </span>
                      <button 
                        onClick={() => handleViewVisitor(visitor.id)}
                        className="p-2 text-gray-400 hover:text-gray-600 min-h-[44px] sm:min-h-[auto] flex items-center justify-center sm:justify-start"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'analytics' && isFeatureAvailable('analytics') && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Visitor Analytics</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-600 mb-2">Today's Check-ins</h3>
              <p className="text-2xl font-bold text-gray-900">{dashboardStats.checked_in_today}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-600 mb-2">Average Visit Duration</h3>
              <p className="text-2xl font-bold text-gray-900">2.5h</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-600 mb-2">Most Visited Building</h3>
              <p className="text-2xl font-bold text-gray-900">Main</p>
            </div>
          </div>
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-600 mb-4">Visitor Trends</h3>
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">Analytics chart would be displayed here</p>
            </div>
          </div>
        </div>
      )}

      {/* Registration Form Modal */}
      {showRegistrationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <VisitorRegistrationForm
              onClose={() => setShowRegistrationForm(false)}
              onSuccess={handleRegistrationSuccess}
            />
          </div>
        </div>
      )}

      {/* Visitor Detail Modal */}
      {showVisitorModal && selectedVisitor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Visitor Details</h2>
                <button
                  onClick={() => setShowVisitorModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Visitor Information */}
              <div className="space-y-4 sm:space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <p className="text-gray-900 text-sm sm:text-base">{selectedVisitor.first_name} {selectedVisitor.last_name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <p className="text-gray-900 text-sm sm:text-base break-all">{selectedVisitor.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                    <p className="text-gray-900 text-sm sm:text-base">{selectedVisitor.company}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      selectedVisitor.status === 'checked_in' ? 'bg-green-100 text-green-800' :
                      selectedVisitor.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      selectedVisitor.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                      selectedVisitor.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedVisitor.status === 'checked_in' ? 'Checked In' :
                       selectedVisitor.status === 'pending' ? 'Pending' :
                       selectedVisitor.status === 'approved' ? 'Approved' :
                       selectedVisitor.status === 'rejected' ? 'Rejected' :
                       'Checked Out'}
                    </span>
                  </div>
                </div>

                {/* Purpose and Host */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Purpose</label>
                    <p className="text-gray-900 text-sm sm:text-base">{selectedVisitor.purpose}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Host Email</label>
                    <p className="text-gray-900 text-sm sm:text-base break-all">{selectedVisitor.host_email}</p>
                  </div>
                </div>

                {/* Visit Times */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Expected Check-in</label>
                    <p className="text-gray-900 text-sm sm:text-base">
                      {selectedVisitor.expected_checkin ? new Date(selectedVisitor.expected_checkin).toLocaleString() : 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Expected Check-out</label>
                    <p className="text-gray-900 text-sm sm:text-base">
                      {selectedVisitor.expected_checkout ? new Date(selectedVisitor.expected_checkout).toLocaleString() : 'Not specified'}
                    </p>
                  </div>
                </div>

                {/* Actual Times */}
                {(selectedVisitor.checked_in_at || selectedVisitor.checked_out_at) && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {selectedVisitor.checked_in_at && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Actual Check-in</label>
                        <p className="text-gray-900 text-sm sm:text-base">{new Date(selectedVisitor.checked_in_at).toLocaleString()}</p>
                      </div>
                    )}
                    {selectedVisitor.checked_out_at && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Actual Check-out</label>
                        <p className="text-gray-900 text-sm sm:text-base">{new Date(selectedVisitor.checked_out_at).toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Photos */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Photos</label>
                  {selectedVisitor.photos && selectedVisitor.photos.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
                      {selectedVisitor.photos.map((photo, index) => (
                        <div key={index} className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                          <img
                            src={photo.file_path || '/api/placeholder/150/150'}
                            alt={photo.description || 'Visitor photo'}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-gray-50 p-3 sm:p-4 rounded-lg text-center">
                      <Camera className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-xs sm:text-sm text-gray-600">No photos uploaded for this visitor</p>
                      <p className="text-xs text-gray-500 mt-1">Photos can be added during registration</p>
                    </div>
                  )}
                </div>

                {/* Documents */}
                {selectedVisitor.documents && selectedVisitor.documents.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Documents</label>
                    <div className="space-y-2">
                      {selectedVisitor.documents.map((doc, index) => (
                        <div key={index} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-gray-50 rounded-lg gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
                            <span className="text-sm text-gray-900 truncate">{doc.file_name}</span>
                          </div>
                          <span className="text-xs text-gray-500 sm:flex-shrink-0">{doc.document_type}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* QR Code */}
                {selectedVisitor.qr_code && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">QR Code</label>
                    <div className="bg-gray-50 p-3 sm:p-4 rounded-lg text-center">
                      <QrCode className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-xs sm:text-sm text-gray-600 break-all">QR Code: {selectedVisitor.qr_code}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="mt-4 sm:mt-6 flex justify-end">
                <button
                  onClick={() => setShowVisitorModal(false)}
                  className="px-4 py-3 sm:py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm sm:text-base min-h-[44px] sm:min-h-[auto]"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitorDashboard;